import { Component, ElementRef, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AnnotationTarget, PDF_ANNOTATION_DATA_SERVICE } from '@app/app/page-modules/pdf-annotation-editor/services/pdf-annotation.service';
import { requestErrorToString } from '@app/app/shared/utils/error-handling.utils';
import { linkedSignal } from '@app/app/signal-backports/linked_signal';
import { resource } from '@app/app/signal-backports/resource';
import { ResourceRef } from '@app/app/signal-backports/resource_api';
import { rxResource } from '@app/app/signal-backports/rx_resource';
import { PdfAnnotationSubtype, PdfDocumentObject, PdfErrorReason, TaskAbortedError, TaskRejectedError } from '@embedpdf/models';
import { TrackedAnnotation } from '@embedpdf/plugin-annotation';
import EmbedPDF, {
  AnnotationCapability,
  AnnotationEvent,
  AnnotationPlugin,
  DocumentManagerCapability,
  DocumentManagerPlugin,
  EmbedPdfContainer,
  GroupItem,
  PluginRegistry,
  ToolbarItem,
  UICapability,
  UIPlugin,
  UISchema,
} from '@embedpdf/snippet';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { AlertSquareIcon } from '@hugeicons/core-free-icons/index';
import { isEqual } from 'lodash-es';
import { Observable, Subject, debounce, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'ptl-embedpdf-annotation-editor',
  standalone: true,
  imports: [HugeiconsIconComponent, MatProgressSpinnerModule, MatButtonModule],
  templateUrl: './embedpdf-annotation-editor.component.html',
  styleUrl: './embedpdf-annotation-editor.component.scss',
})
export class EmbedpdfAnnotationEditorComponent {
  private annotationService = inject(PDF_ANNOTATION_DATA_SERVICE);

  pdfUrl = input.required<string | undefined>();
  annotationTarget = input.required<AnnotationTarget | undefined>();
  readOnly = input(false);
  commentAuthorName = input<string | undefined>(undefined);

  pdfLoadingState = output<PdfLoadingState>();
  annotationLoadingState = output<PdfLoadingState>();
  annotationSavingStateOutput = output<AnnotationSavingState | undefined>();

  protected showSpinner = computed(
    () =>
      (!this.pdfDocument.value() ||
        (this.annotationTarget() && (!this.initialAnnotations.value() || !this.hasAppliedInitialAnnotations()))) &&
      !this.hasError(),
  );
  protected hasError = computed(() => this.pdfDocument.error() || this.initialAnnotations.error());
  protected viewerHidden = computed(() => this.showSpinner() || this.hasError());
  protected pdfDocumentErrorMessage = computed(() => {
    const error = this.pdfDocument.error() as Error;
    if (!error) return undefined;

    return 'PDF loading error: ' + error.toString();
  });
  protected initialAnnotationsErrorMessage = computed(() => {
    const error = this.initialAnnotations.error();
    if (!error) return undefined;

    return 'Annotation loading error: ' + requestErrorToString(error);
  });
  protected secondsUntilRetry = signal<number | undefined>(undefined);
  protected annotationSavingState = linkedSignal<AnnotationSavingState | undefined>(() => {
    this.pdfDocument.value(); // reset on document load
    return undefined;
  });

  private saveRetryTimer: ReturnType<typeof setTimeout> | null = null;

  private pdfViewerContainer = viewChild<ElementRef<HTMLDivElement>>('pdfViewerContainer');
  private embedPdfContainer = signal<EmbedPdfContainer | undefined>(undefined);

  private pluginRegistry: ResourceRef<PluginRegistry> = resource({
    request: () => this.embedPdfContainer(),
    loader: async ({ request: embedPdfContainer }) => {
      const registry = await embedPdfContainer.registry;
      await registry.pluginsReady();
      return registry;
    },
  });
  private pdfDocument: ResourceRef<PdfDocumentObject> = resource({
    request: () => ({
      pdfUrl: this.pdfUrl()?.replaceAll('pdf', 'pdf'),
      pluginsRegistry: this.pluginRegistry.value(),
      readOnly: this.readOnly(),
    }),
    loader: async ({ request: { pdfUrl, pluginsRegistry, readOnly } }) => {
      if (!pdfUrl || !pluginsRegistry) return undefined;

      const documentManager: DocumentManagerCapability = pluginsRegistry
        .getPlugin<DocumentManagerPlugin>(DocumentManagerPlugin.id)
        ?.provides();
      if (!documentManager) return undefined;

      try {
        await documentManager.closeAllDocuments().toPromise();
      } catch (_e) {
        // Don't care about failed close
      }

      this.cancelSaveRetry();

      const openTask = documentManager.openDocumentUrl({ url: pdfUrl, permissions: { overrides: { modifyAnnotations: !readOnly } } });
      const { task: loadTask } = await openTask.toPromise();
      return await loadTask.toPromise();
    },
  });
  private initialAnnotations = rxResource({
    request: () => ({ annotationTarget: this.annotationTarget(), readOnly: this.readOnly() || !this.annotationTarget() }),
    loader: ({ request: { annotationTarget, readOnly } }) => {
      if (!annotationTarget) return undefined;
      return this.annotationService.loadAnnotations(annotationTarget, !readOnly);
    },
  });
  private hasAppliedInitialAnnotations = linkedSignal({
    source: () => [this.annotationTarget(), this.pdfDocument.value()],
    computation: () => false,
  });
  private lastSaveData: object[] | null = null;
  private lastSaveTime: Date | null = null;

  private annotationSaveTriggers$ = new Subject<AnnotationSaveTrigger>();

  private readonly BASE_DISABLED_CATEGORIES = [
    'redaction',
    'document-print',
    'document-export',
    'document-open',
    'document-close',
    'document-screenshot',
    'document-capture',
    'document-protect',
    'rotate',
    'annotation-stamp',
  ];

  private readonly READONLY_DISABLED_CATEGORIES = [
    'annotation-tools-menu',
    'annotation-delete',
    'annotation-markup',
    'panel-annotation-style',
    'shapes-tools-menu',
    'mode-annotate',
    'mode-shapes',
    'mode-view',
  ];

  private readonly ANNOTATION_SUBTYPES_FOR_SAVE = [
    PdfAnnotationSubtype.HIGHLIGHT,
    PdfAnnotationSubtype.TEXT,
    PdfAnnotationSubtype.STRIKEOUT,
    PdfAnnotationSubtype.UNDERLINE,
    PdfAnnotationSubtype.INK,
    PdfAnnotationSubtype.FREETEXT,
    PdfAnnotationSubtype.LINE,
    PdfAnnotationSubtype.SQUARE,
    PdfAnnotationSubtype.CIRCLE,
    PdfAnnotationSubtype.POLYGON,
    PdfAnnotationSubtype.POLYLINE,
  ];

  private readonly AUTO_SAVE_DEBOUNCE_TIME = 1000;

  private readonly MANUAL_SAVE_DEBOUNCE_TIME = 200;

  private readonly SAVE_RETRY_WAIT_TIME = 3;

  constructor() {
    this._initEmbedPDFWhenReady();
    this._applyInitialAnnotationsWhenReady();
    this._triggerAutosaveOnAnnotationChange();
    this._debouncedSaveOnTrigger();
    this._syncUiConfigOnReadOnlyChange();
    this._moveFullscreenButtonToMainToolbar();
    this._outputPdfLoadingStateOnChange();
    this._outputAnnotationLoadingStateOnChange();
    this._outputSavingStateOnChange();
  }

  private _initEmbedPDFWhenReady() {
    effect(() => {
      if (this.embedPdfContainer()) return;
      const element = this.pdfViewerContainer()?.nativeElement;
      if (!element) return;

      const container = EmbedPDF.init({
        target: this.pdfViewerContainer().nativeElement,
        type: 'container',
        documentManager: {
          maxDocuments: 1,
        },
        theme: {
          preference: 'light',
        },
        tabBar: 'never',
        disabledCategories: [...this.BASE_DISABLED_CATEGORIES],
        permissions: {
          overrides: {
            copyContents: true,
            modifyAnnotations: true,
            print: false,
            modifyContents: false,
            fillForms: false,
            assembleDocument: false,
          },
        },
        annotations: {
          // currently annotation author can only be set up on init, no way to change it later - only first input value will take effect
          annotationAuthor: untracked(() => this.commentAuthorName()) || undefined,
        },
      });
      if (container) {
        untracked(() => this.embedPdfContainer.set(container));
      }
    });
  }

  private _applyInitialAnnotationsWhenReady() {
    effect(() => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      const initialAnnotations = this.initialAnnotations.value();
      if (!pluginRegistry || !document || !initialAnnotations || this.hasAppliedInitialAnnotations()) return;
      const annotation: AnnotationCapability = pluginRegistry.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
      if (!annotation) return;

      const toImport = this.readOnly()
        ? (initialAnnotations.committed ?? [])
        : (initialAnnotations.draft ?? initialAnnotations.committed ?? []);

      untracked(() => this.hasAppliedInitialAnnotations.set(true));
      if (toImport.length > 0) {
        console.log('Applying initial annotations', toImport);
        annotation.importAnnotations(toImport.map((x) => ({ annotation: x })));
      }
    });
  }

  private _triggerAutosaveOnAnnotationChange() {
    effect(async () => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      if (!pluginRegistry || !document || !this.hasAppliedInitialAnnotations()) return;

      const annotation: AnnotationCapability = pluginRegistry.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
      annotation.onAnnotationEvent((event) => {
        console.log('annotation event', event);
        if (event.type === 'loaded') return;

        this.annotationSaveTriggers$.next({ type: 'annotationEvent', event, time: new Date() });
      });
    });
  }

  private _debouncedSaveOnTrigger() {
    this.annotationSaveTriggers$
      .pipe(
        filter(({ time }) => !this.lastSaveTime || this.lastSaveTime < time),
        tap(() => {
          this.annotationSavingState.set({ state: 'saving' });
          if (this.saveRetryTimer) {
            clearInterval(this.saveRetryTimer);
            this.saveRetryTimer = null;
            this.secondsUntilRetry.set(undefined);
          }
        }),
        debounce(({ type }) => {
          switch (type) {
            case 'annotationEvent':
              return timer(this.AUTO_SAVE_DEBOUNCE_TIME);
            case 'manualTrigger':
            case 'retry':
              return timer(this.MANUAL_SAVE_DEBOUNCE_TIME);
          }
        }),
        switchMap((trigger) => this.saveAnnotations(trigger)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private _syncUiConfigOnReadOnlyChange() {
    effect(async () => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      if (!pluginRegistry || !document) return;

      const readOnly = this.readOnly();
      const ui: UICapability = pluginRegistry.getPlugin<UIPlugin>(UIPlugin.id)?.provides();
      if (!ui) return;

      if (readOnly) {
        for (const c of this.READONLY_DISABLED_CATEGORIES) {
          ui.disableCategory(c);
        }
      } else {
        for (const c of this.READONLY_DISABLED_CATEGORIES) {
          ui.enableCategory(c);
        }
      }
    });
  }

  private _moveFullscreenButtonToMainToolbar() {
    effect(() => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      if (!pluginRegistry || !document) return;

      const ui: UICapability = pluginRegistry.getPlugin<UIPlugin>(UIPlugin.id)?.provides();
      if (!ui) return;

      const originalSchema: UISchema = ui.getSchema();
      const mainToolbarItems: ToolbarItem[] = [...originalSchema.toolbars['main-toolbar'].items];
      if (!mainToolbarItems) return;

      const leftGroupIdx = mainToolbarItems.findIndex((it) => it.id === 'left-group');
      if (leftGroupIdx < 0) return;
      const leftGroup: GroupItem = JSON.parse(JSON.stringify(mainToolbarItems[leftGroupIdx]));
      mainToolbarItems[leftGroupIdx] = leftGroup;

      const documentMenuButtonIdx = leftGroup.items.findIndex((it) => it.id === 'document-menu-button');
      if (documentMenuButtonIdx < 0) return;

      leftGroup.items.splice(documentMenuButtonIdx, 0, {
        type: 'command-button',
        id: 'document-fullscreen-button',
        commandId: 'document:fullscreen',
        variant: 'icon',
        categories: ['document', 'document-fullscreen'],
      });

      ui.mergeSchema({
        toolbars: {
          'main-toolbar': {
            items: mainToolbarItems,
          },
        },
      });
      ui.disableCategory('document-menu');
    });
  }

  private _outputPdfLoadingStateOnChange() {
    effect(() => {
      if (this.pdfDocument.isLoading()) {
        this.pdfLoadingState.emit({ state: 'loading' });
      } else if (this.pdfDocument.error()) {
        const error = this.pdfDocument.error();
        if (error instanceof TaskRejectedError || error instanceof TaskAbortedError) {
          this.pdfLoadingState.emit({
            state: 'failed',
            error: (error.reason as PdfErrorReason).message,
          });
        } else {
          this.pdfLoadingState.emit({
            state: 'failed',
            error: error.toString() || 'unknown error',
          });
        }
      } else if (this.pdfDocument.value()) {
        this.pdfLoadingState.emit({ state: 'loaded' });
      } else {
        this.pdfLoadingState.emit({ state: 'idle' });
      }
    });
  }

  private _outputAnnotationLoadingStateOnChange() {
    effect(() => {
      if (this.initialAnnotations.isLoading()) {
        this.annotationLoadingState.emit({ state: 'loading' });
      } else if (this.initialAnnotations.error()) {
        this.annotationLoadingState.emit({
          state: 'failed',
          error: requestErrorToString(this.initialAnnotations.error()),
        });
      } else if (this.initialAnnotations.value()) {
        this.annotationLoadingState.emit({ state: this.hasAppliedInitialAnnotations() ? 'loaded' : 'loading' });
      } else {
        this.annotationLoadingState.emit({ state: 'idle' });
      }
    });
  }

  private _outputSavingStateOnChange() {
    effect(() => this.annotationSavingStateOutput.emit(this.annotationSavingState()));
  }

  private saveAnnotations(trigger: AnnotationSaveTrigger): Observable<void> {
    const currentPdfUrl = this.pdfUrl();
    const currentTarget = this.annotationTarget();
    if (!currentPdfUrl || !currentTarget || this.readOnly() || !this.pdfDocument.value() || !this.hasAppliedInitialAnnotations()) {
      return of(void 0);
    }

    const annotations = this.getCurrentAnnotations();
    if (this.lastSaveData && isEqual(annotations, this.lastSaveData)) {
      console.log('Not saving annotations, nothing changed since last save');
      this.annotationSavingState.set({ state: 'saved' });
      return of(void 0);
    }

    const prevSaveTime = this.lastSaveTime;
    this.lastSaveTime = new Date();
    this.lastSaveData = annotations;
    this.annotationSavingState.set({ state: 'saving' });

    console.log('Saving annotations', this.lastSaveTime, annotations);

    return this.annotationService
      .saveAnnotations({
        target: currentTarget,
        isDraft: true,
        annotations,
      })
      .pipe(
        tap({
          next: (saved) => {
            untracked(() => {
              this.initialAnnotations.set({
                committed: this.initialAnnotations.value()?.committed,
                draft: annotations,
              });
            });
            this.annotationSavingState.set({
              state: 'saved',
            });
          },
          error: (error) => {
            this.lastSaveTime = prevSaveTime;
            this.annotationSavingState.set({
              state: 'failed',
              error: requestErrorToString(error),
            });
            this.setupSaveRetry(trigger);
          },
        }),
        map(() => void 0),
        catchError(() => of(void 0)),
      );
  }

  private setupSaveRetry(trigger: AnnotationSaveTrigger) {
    if (this.saveRetryTimer) return;

    this.secondsUntilRetry.set(this.SAVE_RETRY_WAIT_TIME);
    this.saveRetryTimer = setInterval(() => {
      const remaining = this.secondsUntilRetry();
      if (remaining <= 1) {
        this.cancelSaveRetry();
        this.annotationSaveTriggers$.next({
          type: 'retry',
          time: new Date(),
          originalTrigger: trigger,
        });
      } else {
        this.secondsUntilRetry.set(remaining - 1);
      }
    }, 1000);
  }

  private cancelSaveRetry() {
    clearInterval(this.saveRetryTimer);
    this.saveRetryTimer = null;
    this.secondsUntilRetry.set(undefined);
  }

  private getCurrentAnnotations(): object[] {
    const annotation: AnnotationCapability = this.pluginRegistry.value()?.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
    if (!annotation) return [];
    return Object.values<TrackedAnnotation>(annotation.getState().byUid)
      .filter((x) => x.commitState !== 'deleted' && this.ANNOTATION_SUBTYPES_FOR_SAVE.includes(x.object.type))
      .map((x) => x.object);
  }

  protected retryLoading() {
    if (this.pdfUrl() && this.pdfDocument.error()) {
      this.pdfDocument.reload();
    }
    if (this.annotationTarget() && this.initialAnnotations.error()) {
      this.initialAnnotations.reload();
    }
  }

  protected readonly AlertSquareIcon = AlertSquareIcon;
}

export type PdfLoadingState =
  | { state: 'idle' }
  | {
      state: 'loading';
    }
  | {
      state: 'loaded';
    }
  | {
      state: 'failed';
      error: string;
    };

type AnnotationSaveTrigger =
  | {
      type: 'annotationEvent';
      event: AnnotationEvent;
      time: Date;
    }
  | {
      type: 'manualTrigger';
      time: Date;
    }
  | {
      type: 'retry';
      time: Date;
      originalTrigger: AnnotationSaveTrigger;
    };
export type AnnotationSavingState =
  | {
      state: 'unsaved';
    }
  | {
      state: 'saved';
    }
  | {
      state: 'saving';
    }
  | {
      state: 'failed';
      error: string;
    };
